import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Form,
  Button,
  Card,
  Row,
  Col,
  Table,
  Alert,
  ProgressBar,
  Badge,
  Spinner,
} from 'react-bootstrap';
import { UserRound, CalendarCheck, Clock3, BadgeCheck } from 'lucide-react';
import Navbar from './Navbar';
import { API_ENDPOINTS } from './config/api';
import PageTitle from './components/PageTitle';
import useNavigationWarning from './hooks/useNavigationWarning';
import NavigationWarningModal from './components/NavigationWarningModal';
import { getRatingOptions } from "./utils/ratingScales";

const EvaluatorEvaluationForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [evaluation, setEvaluation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState({});
  const [comments, setComments] = useState({});
  const [isStarted, setIsStarted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [activeEntryIndex, setActiveEntryIndex] = useState(0);
  const [bookmarks, setBookmarks] = useState(new Set());

  // Save progress function
  const handleSaveProgress = async () => {
    try {
      const token = localStorage.getItem("token");
      const saveData = {
        answers,
        comments,
        timeLeft,
        timerRunning,
        bookmarks: Array.from(bookmarks),
      };
      
      const res = await fetch(
        `${API_ENDPOINTS.DIRECTOR}/evaluations/${id}/save-progress`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(saveData),
        }
      );

      if (res.ok) {
        return true; // Return success for navigation warning
      } else {
        const error = await res.json();
        throw new Error(error.message || "Error saving progress.");
      }
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  // Save timer state only (for when leaving without saving answers)
  const handleSaveTimerOnly = async () => {
    try {
      const token = localStorage.getItem("token");
      
      // First, get the current saved progress to preserve existing answers
      const getCurrentProgress = await fetch(
        `${API_ENDPOINTS.DIRECTOR}/evaluations/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      let existingAnswers = {};
      let existingComments = {};
      let existingBookmarks = [];
      
      if (getCurrentProgress.ok) {
        const currentData = await getCurrentProgress.json();
        existingAnswers = currentData.progress?.answers || {};
        existingComments = currentData.progress?.comments || {};
        existingBookmarks = currentData.progress?.bookmarks || [];
      }
      
      // Merge current bookmarks with existing ones (use current state if available, otherwise use existing)
      const bookmarksToSave = bookmarks.size > 0 ? Array.from(bookmarks) : existingBookmarks;
      
      const res = await fetch(
        `${API_ENDPOINTS.DIRECTOR}/evaluations/${id}/save-progress`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            answers: existingAnswers, // Preserve existing answers
            comments: existingComments, // Preserve existing comments
            timeLeft,
            timerRunning,
            bookmarks: bookmarksToSave,
          }),
        }
      );

      if (res.ok) {
        return true;
      } else {
        const error = await res.json();
        throw new Error(error.message || "Error saving timer state.");
      }
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  // Navigation warning system
  const {
    showWarningModal,
    handleSaveAndLeave,
    handleLeaveWithoutSaving,
    handleCancelNavigation,
    isTimed,
    timeLeft: warningTimeLeft
  } = useNavigationWarning({
    isActive: isStarted, // Only active when evaluation is started
    evaluationData: { answers, comments, timeLeft },
    onSaveProgress: handleSaveProgress,
    isTimed: evaluation?.template?.isTimed || false,
    onSaveTimer: handleSaveTimerOnly // Separate function for saving timer state only
  });

  useEffect(() => {
    const fetchEvaluation = async () => {
      try {
        const token = localStorage.getItem('token');
        const userRole = localStorage.getItem('role');

        // Check if user is authorized
        if (!token || (userRole !== 'director' && userRole !== 'attending')) {
          setError('You are not authorized to access this evaluation');
          setLoading(false);
          return;
        }

        const response = await fetch(`${API_ENDPOINTS.DIRECTOR}/evaluations/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error('Error response:', errorData);
          throw new Error(errorData.message || 'Failed to fetch evaluation');
        }

        const data = await response.json();
        setEvaluation(data);
        
        // Initialize answers object with empty values for each question
        const initialAnswers = {};
        const initialComments = {};
        data.template.questions.forEach(question => {
          if (question.questionType === 'checkbox') {
            initialAnswers[question.id] = [];
          } else if (question.questionType === 'radio-matrix') {
            initialAnswers[question.id] = {};
          } else {
            initialAnswers[question.id] = '';
          }
          // Initialize comments for feedback evaluations
          if (data.template.evaluationType === 'feedback') {
            initialComments[question.id] = '';
          }
        });

        // Check for saved progress and restore it
        if (data.progress && data.progress.answers) {
          setAnswers({ ...initialAnswers, ...data.progress.answers });
          
          if (data.progress.comments) {
            setComments({ ...initialComments, ...data.progress.comments });
          }
          
          // Restore bookmarks if they exist
          if (data.progress.bookmarks && Array.isArray(data.progress.bookmarks)) {
            setBookmarks(new Set(data.progress.bookmarks));
          }
          
          // Restore timer state if it's a timed evaluation
          if (data.template.isTimed && data.progress.timeLeft !== null) {
            setTimeLeft(data.progress.timeLeft);
            setTimerRunning(data.progress.timerRunning || false);
          }
        } else {
          setAnswers(initialAnswers);
          setComments(initialComments);
        }
        setIsStarted(false);

        // Initialize timer if evaluation is timed (but don't start it yet)
        if (data.template.isTimed && (!data.progress || data.progress.timeLeft === null)) {
          setTimeLeft(data.template.timeLimit * 60); // Convert minutes to seconds
          setTimerRunning(false); // Don't start timer automatically
        }
      } catch (err) {
        console.error('Error fetching evaluation:', err);
        setError(err.message);
      }
      setLoading(false);
    };

    fetchEvaluation();
  }, [id]);

  // Timer effect
  useEffect(() => {
    let interval;
    if (timerRunning && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setTimerRunning(false);
            handleSubmit(); // Auto-submit when time runs out
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [timerRunning, timeLeft]);

  // Format time display
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const handleStartEvaluation = () => {
    setIsStarted(true);
    // Start the timer when evaluation begins
    if (evaluation.template.isTimed) {
      setTimerRunning(true);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleCommentChange = (questionId, value) => {
    setComments(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleToggleBookmark = (questionId) => {
    setBookmarks(prev => {
      const newBookmarks = new Set(prev);
      if (newBookmarks.has(questionId)) {
        newBookmarks.delete(questionId);
      } else {
        newBookmarks.add(questionId);
      }
      return newBookmarks;
    });
  };

  const handleSaveProgressAndNavigate = async () => {
    try {
      await handleSaveProgress();
      alert("Progress saved!");
      
      // Navigate based on user role
      const userRole = localStorage.getItem('role');
      const navigatePath = userRole === 'attending' ? '/attending/evaluations' : '/evaluations';
      navigate(navigatePath);
    } catch (err) {
      console.error(err);
      alert("Failed to save progress");
    }
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
    }
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const userRole = localStorage.getItem('role');
      
      // Format answers to ensure they match the expected format
      const formattedAnswers = {};
      Object.entries(answers).forEach(([questionId, value]) => {
        // Skip empty answers
        if (value === '' || value === null || value === undefined) {
          return;
        }
        
        // For checkbox questions, ensure it's an array
        if (Array.isArray(value)) {
          if (value.length > 0) {
            formattedAnswers[questionId] = value;
          }
        }
        // For radio-matrix questions, ensure it's an object with numeric values
        else if (typeof value === 'object' && value !== null) {
          const formattedMatrix = {};
          Object.entries(value).forEach(([key, val]) => {
            if (val !== null && val !== undefined) {
              formattedMatrix[key] = parseInt(val);
            }
          });
          if (Object.keys(formattedMatrix).length > 0) {
            formattedAnswers[questionId] = formattedMatrix;
          }
        }
        // For all other question types, use the value as is
        else {
          formattedAnswers[questionId] = value;
        }
      });

      const response = await fetch(`${API_ENDPOINTS.DIRECTOR}/evaluations/${id}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          answers: formattedAnswers,
          comments: evaluation.template.evaluationType === 'feedback' ? comments : null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to submit evaluation');
      }

      // Redirect based on user role
      if (userRole === 'attending') {
        navigate('/attending/evaluations');
      } else {
        navigate('/evaluations');
      }
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  // Helper: get all numbered questions (exclude header-paragraph and divider)
  const numberedQuestions = evaluation?.template.questions.filter(
    (q) => !["header-paragraph", "divider"].includes(q.questionType)
  ) || [];

  // Helper: check if a question is filled out
  const isQuestionFilled = (q) => {
    if (q.questionType === "checkbox") {
      return Array.isArray(answers[q.id]) && answers[q.id].length > 0;
    }
    if (q.questionType === "radio-matrix") {
      return (
        answers[q.id] && Object.keys(answers[q.id]).length === q.options.length
      );
    }
    return (
      answers[q.id] !== undefined &&
      answers[q.id] !== "" &&
      answers[q.id] !== null
    );
  };

  // Progress calculation
  const totalQuestions = numberedQuestions.length;
  const filledQuestions = numberedQuestions.filter(isQuestionFilled).length;
  const progressPercent =
    totalQuestions > 0
      ? Math.round((filledQuestions / totalQuestions) * 100)
      : 0;
  const hasQuestions = totalQuestions > 0;

  // Process questions into sections and navigation entries
  const questionMap = useMemo(() => {
    if (!evaluation?.template?.questions) return {};
    const map = {};
    numberedQuestions.forEach((q, idx) => {
      map[q.id] = { question: q, index: idx };
    });
    return map;
  }, [evaluation?.template?.questions, numberedQuestions]);

  const sections = useMemo(() => {
    if (!evaluation?.template?.questions) return [];
    const list = [];
    let currentSectionBlock = null;
    let fallbackIndex = 1;

    const startFallbackSection = () => {
      currentSectionBlock = {
        id: `section-${fallbackIndex}`,
        header: `Section ${fallbackIndex}`,
        paragraph: "",
        questionIds: [],
        hasOverviewContent: false,
      };
      fallbackIndex += 1;
      list.push(currentSectionBlock);
    };

    evaluation.template.questions.forEach((question) => {
      if (question.questionType === "header-paragraph") {
        let header = "";
        let paragraph = "";
        try {
          const parsed = JSON.parse(question.questionText);
          header = parsed.header || "";
          paragraph = parsed.paragraph || "";
        } catch (e) {
          header = question.header || "";
          paragraph = question.paragraph || "";
        }
        currentSectionBlock = {
          id: question.id || `section-${fallbackIndex}`,
          header: header || `Section ${fallbackIndex}`,
          paragraph: paragraph || "",
          questionIds: [],
          hasOverviewContent:
            Boolean(header && header.trim()) ||
            Boolean(paragraph && paragraph.trim()),
        };
        fallbackIndex += 1;
        list.push(currentSectionBlock);
        return;
      }
      if (question.questionType === "divider") {
        return;
      }
      if (!currentSectionBlock) {
        startFallbackSection();
      }
      currentSectionBlock.questionIds.push(question.id);
    });

    return list;
  }, [evaluation?.template?.questions]);

  const { navigationEntries, sectionEntryIndexMap } = useMemo(() => {
    if (!evaluation?.template?.questions) return { navigationEntries: [], sectionEntryIndexMap: {} };
    const entries = [];
    const sectionIndexMap = {};

    // Add top-level introduction entry if introHtml exists
    if (evaluation.template.introHtml && evaluation.template.introHtml.trim()) {
      entries.push({
        type: "intro",
        id: "intro",
        sectionIndex: null,
      });
    }

    sections.forEach((section, idx) => {
      const sectionKey = section.id || `section-${idx + 1}`;
      if (section.hasOverviewContent) {
        const overviewIndex = entries.length;
        entries.push({
          type: "overview",
          id: `overview-${sectionKey}`,
          sectionIndex: idx,
          header: section.header,
          paragraph: section.paragraph,
        });
        sectionIndexMap[sectionKey] = overviewIndex;
      }

      section.questionIds.forEach((questionId) => {
        const mapEntry = questionMap[questionId];
        if (mapEntry) {
          entries.push({
            type: "question",
            id: questionId,
            sectionIndex: idx,
            question: mapEntry.question,
            questionNumber: mapEntry.index + 1,
          });
        }
      });
    });

    return {
      navigationEntries: entries,
      sectionEntryIndexMap: sectionIndexMap,
    };
  }, [sections, questionMap]);

  useEffect(() => {
    if (navigationEntries.length === 0) {
      setActiveEntryIndex(0);
      return;
    }
    if (activeEntryIndex > navigationEntries.length - 1) {
      setActiveEntryIndex(navigationEntries.length - 1);
    }
  }, [navigationEntries, activeEntryIndex]);

  const activeEntry = navigationEntries[activeEntryIndex] || null;
  const currentQuestion =
    activeEntry?.type === "question" ? activeEntry.question : null;
  const displayQuestionNumber =
    activeEntry?.type === "question" ? activeEntry.questionNumber : 0;
  const activeSectionIndex =
    typeof activeEntry?.sectionIndex === "number"
      ? activeEntry.sectionIndex
      : sections.length > 0
      ? 0
      : -1;

  const activeSectionMeta =
    activeSectionIndex >= 0 ? sections[activeSectionIndex] : sections[0] || null;
  const activeSectionTitle = activeSectionMeta?.header || evaluation?.template?.title || "";
  const activeSectionParagraph = activeSectionMeta?.paragraph || "";
  const isIntroActive = activeEntry?.type === "intro";
  const isOverviewActive = activeEntry?.type === "overview";
  const totalEntries = navigationEntries.length;
  const allQuestionsAnswered =
    totalQuestions > 0 &&
    numberedQuestions.every((q) => isQuestionFilled(q));

  const handleNextEntry = () => {
    if (!navigationEntries.length) return;
    setActiveEntryIndex((prev) =>
      Math.min(prev + 1, navigationEntries.length - 1)
    );
  };

  const handlePreviousEntry = () => {
    if (!navigationEntries.length) return;
    setActiveEntryIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleSectionClick = (section, sectionIdx) => {
    if (!section) return;
    const sectionKey = section.id || `section-${sectionIdx + 1}`;
    const targetIndex = sectionEntryIndexMap[sectionKey];
    if (typeof targetIndex === "number" && targetIndex >= 0) {
      setActiveEntryIndex(targetIndex);
    }
  };

  const renderQuestion = (q, questionNumber) => {
    if (!q) {
      return (
        <div className="text-muted py-4">
          No questions available for this section.
        </div>
      );
    }

    return (
      <Form.Group className="mb-4">
        <div className="d-flex flex-column align-items-start justify-content-start gap-3 mb-3">
            <div className="d-flex flex-row align-items-center justify-content-between w-100">
              <div className="d-flex flex-row align-items-center justify-content-start gap-2">
                <span className="bg-secondary fw-semibold px-3 py-2 rounded-3 small">Question {questionNumber}</span>
                <Button 
                  variant="light" 
                  size="sm" 
                  className="px-2 py-1"
                  onClick={() => handleToggleBookmark(q.id)}
                  style={{ color: bookmarks.has(q.id) ? 'var(--bs-danger)' : 'inherit' }}
                >
                  <i className={`bi ${bookmarks.has(q.id) ? 'bi-bookmark-fill' : 'bi-bookmark'}`}></i>
                </Button>
                {evaluation.template.evaluationType === 'feedback' && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="px-2 py-1"
                    onClick={() => {
                      const commentField = document.getElementById(`comment-${q.id}`);
                      if (commentField) {
                        commentField.style.display = commentField.style.display === 'none' ? 'block' : 'none';
                      }
                    }}
                  >
                    <i className="bi bi-chat-dots"></i>
                    <span className="d-none d-md-inline ms-2">Add Comment</span>
                  </Button>
                )}
              </div>
            <span className="text-muted small">
              {hasQuestions
                ? `${displayQuestionNumber} of ${totalQuestions}`
                : "No questions"}
            </span>
          </div>
          {(q.showInfo || (q.infoHtml && String(q.infoHtml).trim())) && q.infoPlacement === "before" && (
            <div className="mb-3 p-3 rounded-3 bg-light small eval-rich-content" style={{ lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: q.infoHtml || "" }} />
          )}
          <h5 className="fw-semibold mb-0">{q.questionText}</h5>
        </div>

        {q.questionType === "text" && (
          <Row>
            <Col md={12} className="mb-3">
              <Form.Control
                type="text"
                className="bg-light rounded-3"
                value={answers[q.id] || ""}
                onChange={(e) => handleAnswerChange(q.id, e.target.value)}
              />
            </Col>
          </Row>
        )}
        {q.questionType === "textarea" && (
          <Row>
            <Col md={12}>
              <Form.Control
                as="textarea"
                rows={4}
                className="bg-light rounded-3"
                value={answers[q.id] || ""}
                onChange={(e) => handleAnswerChange(q.id, e.target.value)}
              />
            </Col>
          </Row>
        )}
        {q.questionType === "radio" && (
          <div className="d-flex flex-column gap-2">
            {q.options.map((option, i) => (
              <Button
                key={i}
                variant={answers[q.id] === option ? "subtle" : "light-solid"}
                className="d-flex gap-2 p-3"
                onClick={() => handleAnswerChange(q.id, option)}
              >
                <Form.Check
                  type="radio"
                  id={`radio-${q.id}-${i}`}
                  name={`q${q.id}`}
                  checked={answers[q.id] === option}
                  onChange={() => {}}
                  style={{ pointerEvents: "none" }}
                  className="me-2 mb-0"
                />
                <span className="text-nowrap">{option}</span>
              </Button>
            ))}
          </div>
        )}
        {q.questionType === "checkbox" && (
          <Row>
            <Col md={12} className="mb-3">
              {q.options.map((option, i) => (
                <Row key={i}>
                  <Col md={12} className="d-flex flex-column flex-start">
                    <Button
                      size="md"
                      variant={(answers[q.id] || []).includes(option) ? "subtle" : "light-solid"}
                      className="d-flex mb-2 gap-2 p-3"
                      onClick={() => {
                        const currentAnswers = answers[q.id] || [];
                        if (currentAnswers.includes(option)) {
                          handleAnswerChange(
                            q.id,
                            currentAnswers.filter((a) => a !== option)
                          );
                        } else {
                          handleAnswerChange(q.id, [...currentAnswers, option]);
                        }
                      }}
                    >
                      <Form.Check
                        type="checkbox"
                        id={`checkbox-${q.id}-${i}`}
                        label=""
                        value={option}
                        checked={(answers[q.id] || []).includes(option)}
                        onChange={(e) => e.stopPropagation()}
                        style={{ pointerEvents: "none" }}
                      />
                      {option}
                    </Button>
                  </Col>
                </Row>
              ))}
            </Col>
          </Row>
        )}
        {q.questionType === "dropdown" && (
          <Row>
            <Col md={6}>
              <Form.Select
                value={answers[q.id] || ""}
                onChange={(e) => handleAnswerChange(q.id, e.target.value)}
              >
                <option value="">Select an option</option>
                {q.options.map((option, i) => (
                  <option key={i} value={option}>
                    {option}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
        )}
        {q.questionType === "rating" && (
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {getRatingOptions(q).map((opt) => (
              <Button
                key={String(opt.value)}
                variant={String(answers[q.id]) === String(opt.value) ? "subtle" : "light-solid"}
                className="d-flex gap-3 p-3 w-100"
                onClick={() => handleAnswerChange(q.id, opt.value)}
              >
                <Form.Check
                  type="radio"
                  id={`rating-${q.id}-${String(opt.value)}`}
                  name={`rating-${q.id}`}
                  checked={String(answers[q.id]) === String(opt.value)}
                  onChange={() => {}}
                  style={{
                    pointerEvents: "none",
                    marginBottom: "0.25rem",
                  }}
                  className="m-0"
                />
                <span>{opt.label}</span>
              </Button>
            ))}
          </div>
        )}
        {q.questionType === "radio-matrix" && q.options.length > 0 && (
          <div className="table-responsive custom-modern-responsive custom-modern-responsive-hover">
            <Table>
              <thead>
                <tr>
                  <th className="px-3" style={{ width: "40%" }}>
                    Performance Factors
                  </th>
                  {(q.columns && q.columns.length > 0
                    ? q.columns
                    : [
                        "Unsatisfactory",
                        "Needs Improvement",
                        "Meets Expectations",
                        "Exceeds Expectations",
                      ]
                  ).map((col, colIdx) => (
                    <th key={colIdx}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {q.options.map((opt, idx) => (
                  <tr key={idx}>
                    <td className="fw-medium px-3">
                      {opt || `Factor ${idx + 1}`}
                    </td>
                    {(q.columns && q.columns.length > 0
                      ? q.columns
                      : [
                          "Unsatisfactory",
                          "Needs Improvement",
                          "Meets Expectations",
                          "Exceeds Expectations",
                        ]
                    ).map((_, colIdx) => (
                      <td key={colIdx} className="text-center">
                        <Form.Check
                          type="radio"
                          name={`matrix-${q.id}-${idx}`}
                          checked={answers[q.id]?.[idx] === colIdx}
                          onChange={() =>
                            handleAnswerChange(q.id, {
                              ...(answers[q.id] || {}),
                              [idx]: colIdx,
                            })
                          }
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {/* Comment field for feedback evaluations */}
        {evaluation.template.evaluationType === 'feedback' && (
          <div 
            id={`comment-${q.id}`} 
            className="mt-3"
            style={{ display: 'none' }}
          >
            <Form.Group>
              <Form.Label className="fw-bold">
                Comment:
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Add your comment here..."
                value={comments[q.id] || ""}
                onChange={(e) => handleCommentChange(q.id, e.target.value)}
              />
            </Form.Group>
          </div>
        )}
        {(q.showInfo || (q.infoHtml && String(q.infoHtml).trim())) && q.infoPlacement === "after" && (
          <div className="mt-3 p-3 rounded-3 bg-light small eval-rich-content" style={{ lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: q.infoHtml || "" }} />
        )}
      </Form.Group>
    );
  };

  if (loading) {
    return (
      <div className="min-vh-100">
        <PageTitle title="Loading..." />
        <Navbar />
        <Container className="py-4 text-center">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
            <div className="text-center">
              <Spinner animation="border" size="lg" className="mb-3" />
              <div className="text-muted">Loading evaluation...</div>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-vh-100">
        <PageTitle title="Evaluation Error" />
        <Navbar />
        <Container className="py-4">
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <Alert variant="danger">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error}
              </Alert>
            </Card.Body>
          </Card>
        </Container>
      </div>
    );
  }

  if (evaluation && evaluation.status === 'completed') {
    return (
      <div className="min-vh-100">
        <PageTitle title="Evaluation Completed" />
        <Navbar />
        <Container className="py-4">
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="mb-4">
                <i className="bi bi-check-circle text-success" style={{ fontSize: '4rem' }}></i>
              </div>
              <h3 className="mb-3">{evaluation.title || evaluation.template.title}</h3>
              <Alert variant="success" className="d-inline-block">
                <i className="bi bi-check-circle me-2"></i>
                This evaluation has already been completed.
              </Alert>
            </Card.Body>
          </Card>
        </Container>
      </div>
    );
  }

  if (!isStarted) {
    const hasProgress =
      !!evaluation?.progress &&
      !!(
        (evaluation.progress.answers &&
          Object.keys(evaluation.progress.answers).length > 0) ||
        (evaluation.progress.timeLeft !== null &&
          evaluation.progress.timeLeft !== undefined)
      );

    const timeDisplay =
      evaluation?.template?.isTimed && timeLeft > 0
        ? `${Math.floor(timeLeft / 60)
            .toString()
            .padStart(2, "0")}:${(timeLeft % 60)
            .toString()
            .padStart(2, "0")}`
        : evaluation?.template?.isTimed && evaluation.template.timeLimit
        ? `${evaluation.template.timeLimit.toString().padStart(2, "0")}:00`
        : null;

    return (
      <div className="min-vh-100">
        <PageTitle title="Start Evaluation" />
        <Navbar />
        <Container className="py-4 d-flex align-items-center justify-content-center">
          {/* Header: title, badge, description */}

          {/* Main card: progress, details, alert */}
          <Row>
            <Col md={12}>
          <Card className="border-0 shadow-sm rounded-3 mb-3">
            <Card.Header className="px-4 pt-4 d-flex flex-md-row flex-column align-items-center justify-content-between border-0 ">
              <div className="d-flex flex-column align-items-start justify-content-start gap-2">
                <Badge
                bg={
                  evaluation?.template?.evaluationType === "survey"
                    ? "info"
                    : evaluation?.template?.evaluationType === "performance"
                    ? "primary"
                    : evaluation?.template?.evaluationType === "feedback"
                    ? "info"
                    : "secondary"
                }
                className="rounded-pill px-3 py-2"
              >
                {evaluation?.template?.evaluationType === "performance"
                  ? "Performance"
                  : evaluation?.template?.evaluationType === "feedback"
                  ? "Feedback"
                  : evaluation?.template?.evaluationType === "survey"
                  ? "Survey"
                  : evaluation?.template?.evaluationType === "questionnaire"
                  ? "Questionnaire"
                  : evaluation?.template?.evaluationType || "Evaluation"}
              </Badge>
              <h4
                className="mb-0 fw-bold text-dark"
                style={{ fontSize: "1.5rem" }}
              >
                {evaluation?.title || evaluation?.template?.title}
              </h4>
              
             </div>
              <Button
            variant="primary"
            size="sm"
            onClick={handleStartEvaluation}
            className="rounded-3 fw-semibold d-flex align-items-center justify-content-center gap-2"
          >
            <i
              className={`bi ${
                hasProgress ? "bi-arrow-clockwise" : "bi-send"
              } fs-5`}
            />
            {hasProgress ? "Continue Evaluation" : "Start Evaluation"}
          </Button>
            </Card.Header>
            <Card.Body className="px-md-4 pb-5">
            <div className="mb-4">
                  
              {evaluation?.template?.description &&
                evaluation.template.description.trim() && (
                  <div className="mb-4">
                    <span className="text-muted fw-semibold small">Description:</span>
                    <p className="mb-0">{evaluation.template.description}</p>
                  </div>
                )}
              </div>
              <hr className="my-4" />
              
              {/* Resident, Due Date, Time Remaining */}
              <Row className="g-3 mb-4">
                <Col md={3}>
                  <div className="d-flex align-items-start gap-3">
                    <div className="icon-container-lg">
                      <UserRound />
                    </div>
                    <div>
                      <div className="small text-muted fw-semibold">Resident</div>
                      <div className="fw-semibold text-dark">
                        {evaluation?.resident
                          ? `${evaluation.resident.firstName} ${evaluation.resident.lastName}`
                          : "Resident"}
                      </div>
                    </div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="d-flex align-items-start gap-3">
                    <div className="icon-container-lg">
                      <CalendarCheck />
                    </div>
                    <div>
                      <div className="small text-muted fw-semibold">Due Date</div>
                      <div className="fw-semibold text-dark">
                        {evaluation?.dueDate
                          ? new Date(evaluation.dueDate).toLocaleDateString(
                              undefined,
                              {
                                month: "long",
                                day: "numeric",
                                year: "numeric",
                              }
                            )
                          : "Date to be announced"}
                      </div>
                    </div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="d-flex align-items-start gap-3">
                    <div className="icon-container-lg">
                      <Clock3 />
                    </div>
                    <div>
                      <div className="small text-muted fw-semibold">
                        Time Remaining
                      </div>
                      <div className="fw-semibold text-dark">
                        {timeDisplay != null ? timeDisplay : "Untimed"}
                      </div>
                    </div>
                  </div>
                </Col>
                <Col md={3}>
                <div className="d-flex align-items-start gap-3">
                    <div className="icon-container-lg">
                      <BadgeCheck />
                    </div>
                    <div>
                    <div className="small text-muted fw-semibold">
                        Status
                      </div>
                  <Badge bg={evaluation.status === 'completed' ? 'success' : 'primary'}>
                    {evaluation.status === 'completed' ? 'Completed' : 'In Progress'}
                  </Badge>
                  </div></div>
                </Col>
              </Row>

              <Row>
                <Col>
                  {/* Progress */}
                  <div className="mb-4 bg-light rounded-3 px-4 py-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="fw-semibold text-dark">Progress</div>
                      <div className="text-muted small">
                        {progressPercent}% Complete, {filledQuestions} of {totalQuestions} questions answered
                      </div>
                    </div>
                    <ProgressBar
                      now={progressPercent}
                      variant="primary"
                      style={{ height: 6 }}
                      className="rounded-pill"
                    />
                  </div>
                </Col>
              </Row>
              {/* Auto-save info alert */}
              <Alert
                variant="info"
                className="mb-0 d-flex align-items-start gap-2 rounded-3 border-0"
                style={{ backgroundColor: "var(--bs-info-bg-subtle)" }}
              >
                <i
                  className="bi bi-info-circle-fill text-info mt-1"
                  style={{ fontSize: "1.1rem" }}
                />
                <div>
                  <span className="fw-semibold text-info">
                    Your need to save your progress before exiting. 
                  </span>
                  <span className="text-info">
                    {" "}
                    You can save your progress and exit anytime
                    before the due date.
                  </span>
                </div>
              </Alert>
            </Card.Body>
          </Card>

          {/* Start / Continue button */}
          <div className="d-flex justify-content-end gap-3">
          <Button variant="light-solid" size="sm" onClick={() => navigate("/evaluations")}>
            <i className="bi bi-arrow-left me-2"></i>
            Back to Evaluations
          </Button>
         </div>
         </Col>
         </Row>
        </Container>
      </div>
    );
  }

  return (
    <div className="min-vh-100">
      <PageTitle title={evaluation?.template?.title || "Evaluation Form"} description="Complete the evaluation for the assigned resident" />
      <Navbar pageTitle={evaluation?.template?.title || "Evaluation Form"} />
      <div className="bg-white shadow-sm custom-sticky-top">
        <Container>
          <Row className="align-items-center">
            <Col lg={evaluation?.template?.isTimed ? 8 : 12} md={evaluation?.template?.isTimed ? 6 : 12} className="d-flex flex-row align-items-center justify-content-between gap-2">
              <div className="d-flex flex-row align-items-center justify-content-start gap-2">
                <h5 className="fw-semibold mb-0 py-3">
                  {evaluation?.title || evaluation?.template?.title}
                </h5>
                <span className="badge bg-secondary small">
                  {evaluation?.template?.evaluationType === 'performance' ? 'Performance' : 'Feedback'}
                </span>
              </div>
              <div className="d-flex flex-wrap gap-4 text-muted small">
                <div className="d-flex align-items-center gap-2">
                  <i className="bi bi-person-circle text-muted" />
                  <span>{evaluation?.resident?.firstName} {evaluation?.resident?.lastName}</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <i className="bi bi-calendar-event text-muted" />
                  <span> Due: {evaluation?.dueDate ? new Date(evaluation.dueDate).toLocaleString() : "Date to be announced"}</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <i className="bi bi-stopwatch" />
                  <span>
                    {evaluation?.template?.isTimed
                      ? `Estimated ${evaluation.template.timeLimit} min`
                      : "Untimed"}
                  </span>
                </div>
              </div>
            </Col>
            {evaluation?.template?.isTimed && (
              <Col
                lg={4}
                md={6}
                className="d-flex justify-content-md-end justify-content-center align-items-center mt-4 mt-md-0"
              >
                <div
                  className="timer-container shadow-sm"
                  style={{
                    background: "var(--main-gradient)",
                    borderRadius: ".9rem",
                    padding: ".9rem",
                    position: "relative",
                    overflow: "hidden",
                    maxWidth: "240px",
                    width: "100%",
                  }}
                >
                  <div
                    className="d-flex justify-content-center align-items-center flex-column"
                    style={{ position: "relative", zIndex: 2 }}
                  >
                    <small className="text-white-50 text-uppercase fw-semibold">
                      Time Remaining
                    </small>
                    <div className="d-flex align-items-center mt-2">
                      <div className="text-center text-white px-2">
                        <h4 className="fw-bold mb-0">
                          {Math.floor(timeLeft / 3600)
                            .toString()
                            .padStart(2, "0")}
                        </h4>
                        <small>Hours</small>
                      </div>
                      <div
                        style={{
                          width: "2px",
                          height: "48px",
                          background: "rgba(255,255,255,0.35)",
                          margin: "0 12px",
                        }}
                      ></div>
                      <div className="text-center text-white px-2">
                        <h4 className="fw-bold mb-0">
                          {Math.floor((timeLeft % 3600) / 60)
                            .toString()
                            .padStart(2, "0")}
                        </h4>
                        <small>Minutes</small>
                      </div>
                      <div
                        style={{
                          width: "2px",
                          height: "48px",
                          background: "rgba(255,255,255,0.35)",
                          margin: "0 12px",
                        }}
                      ></div>
                      <div className="text-center text-white px-2">
                        <h4 className="fw-bold mb-0">
                          {(timeLeft % 60).toString().padStart(2, "0")}
                        </h4>
                        <small>Seconds</small>
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
            )}
          </Row>
        </Container>
      </div>
      <Container className="py-4">
        <Row>
          {/* Sidebar for question list (hidden on xs/sm) */}
          <Col lg={3} md={4} className="d-none d-md-block">
            <Card className="mb-3 border-0 shadow-sm">
              <Card.Header className="fw-bold border-0 px-4 pt-3 mb-2">
                Progress
              </Card.Header>
              <Card.Body className="px-3 pt-0">
                <div className="d-flex flex-column justify-content-between align-items-center gap-2">
                  <div className="flex-grow-1 w-100">
                    <ProgressBar
                      now={progressPercent}
                      label=""
                      style={{
                        width: "100%",
                        height: 6,
                        fontWeight: 600,
                        fontSize: 15,
                      }}
                      variant={
                        progressPercent === 100 ? "bs-success" : "bs-primary"
                      }
                      animated
                    />
                  </div>
                  <div className="d-flex flex-row align-items-center justify-content-between w-100 gap-2">
                    <span className="flex-grow-1 small fw-semibold">
                      {progressPercent}% complete
                    </span>
                    <span className="text-muted small text-end">
                      {filledQuestions}/{totalQuestions || 0} answered
                    </span>
                  </div>
                </div>
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm">
              <Card.Header className="fw-bold border-0 px-4 pt-3 pb-2 mb-2">
                Questions
              </Card.Header>
              <Card.Body className="px-3 pt-0">
                <ul className="list-unstyled mb-0">
                  {navigationEntries.map((entry, idx) => {
                    const isActive = idx === activeEntryIndex;
                    const isQuestionEntry = entry.type === "question";
                    const sectionMeta =
                      typeof entry.sectionIndex === "number"
                        ? sections[entry.sectionIndex]
                        : null;
                    let label;
                    if (entry.type === "intro") {
                      label = "Introduction";
                    } else if (isQuestionEntry) {
                      label =
                        entry.question.questionText ||
                        `Question ${entry.questionNumber}`;
                    } else {
                      label =
                        sectionMeta?.header ||
                        `Section ${typeof entry.sectionIndex === "number" ? entry.sectionIndex + 1 : ""}`;
                    }
                    return (
                      <li key={entry.id || idx} className="mb-2">
                        <Button
                          variant="light"
                          className="d-flex align-items-center justify-content-between p-2 rounded-3"
                          style={{ overflow: "hidden", width: "100%" }}
                          onClick={() => setActiveEntryIndex(idx)}
                        >
                          <div className="d-flex align-items-center gap-3">
                            <div
                              className={`rounded-circle d-flex align-items-center justify-content-center ${
                                isQuestionEntry && isQuestionFilled(entry.question)
                                  ? "bg-success text-white"
                                  : "border text-muted"
                              }`}
                              style={{ width: 32, height: 32 }}
                            >
                              {isQuestionEntry ? (
                                isQuestionFilled(entry.question) ? (
                                  <i className="bi bi-check" />
                                ) : (
                                  <span className="fw-semibold">
                                    {entry.questionNumber}
                                  </span>
                                )
                              ) : (
                                <i className="bi bi-info-lg text-muted" />
                              )}
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <div
                                className="fw-semibold text-truncate small"
                                style={{ maxWidth: "160px" }}
                              >
                                {label}
                              </div>
                              {isQuestionEntry && bookmarks.has(entry.question.id) && (
                                <i className="bi bi-bookmark-fill text-danger" style={{ fontSize: '0.875rem' }}></i>
                              )}
                            </div>
                          </div>
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              </Card.Body>
            </Card>
          </Col>
          {/* Main content */}
          <Col lg={9} md={8}>
            <Card className="mb-4 shadow-sm border-0 rounded-4" style={{ height: `calc(100%)` }}>
              <Card.Body className="px-4 py-4">
                <Form>
                  {isIntroActive ? (
                    evaluation?.template?.introHtml ? (
                      <div
                        className="mb-4 p-3 rounded-3 bg-light border-0 eval-rich-content"
                        style={{ lineHeight: 1.6 }}
                        dangerouslySetInnerHTML={{ __html: evaluation.template.introHtml }}
                      />
                    ) : (
                      <div className="text-muted py-4">
                        No introduction provided for this evaluation.
                      </div>
                    )
                  ) : isOverviewActive ? (
                    <div>
                      <h4 className="fw-bold mb-2">{activeSectionTitle}</h4>
                      {activeSectionParagraph && (
                        <p className="text-muted mb-0">{activeSectionParagraph}</p>
                      )}
                    </div>
                  ) : (
                    <div>
                      {currentQuestion ? (
                        renderQuestion(currentQuestion, displayQuestionNumber)
                      ) : (
                        <div className="text-muted py-4">
                          No question selected.
                        </div>
                      )}
                    </div>
                  )}
                </Form>
              </Card.Body>
              <Card.Footer
                className="d-flex justify-content-between align-items-md-center gap-3 p-4"
                style={{
                  borderTop: "1px dashed var(--border-bg)",
                  backgroundColor: "var(--bs-card-bg)",
                }}
              >
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-success"
                    size="sm"
                    onClick={handleSaveProgressAndNavigate}
                  >
                    <i className="bi bi-floppy2"></i>
                  </Button>
                </div>
                <div className="d-flex justify-content-between align-items-center gap-3">
                  <div className="d-flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handlePreviousEntry}
                      disabled={activeEntryIndex === 0}
                    >
                      <i className="bi bi-arrow-left b-block d-md-none"></i> <span className="d-none d-md-inline">Previous</span>
                    </Button>
                    <Button
                      variant={allQuestionsAnswered ? "success" : "dark"}
                      size="sm"
                      onClick={
                        allQuestionsAnswered ? handleSubmit : handleNextEntry
                      }
                      disabled={
                        allQuestionsAnswered
                          ? false
                          : totalEntries === 0 ||
                            activeEntryIndex === totalEntries - 1
                      }
                    >
                      {allQuestionsAnswered ? "Submit Evaluation" : "Continue"}
                    </Button>
                  </div>
                </div>
              </Card.Footer>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Navigation Warning Modal */}
      <NavigationWarningModal
        show={showWarningModal}
        onHide={handleCancelNavigation}
        onSaveAndLeave={handleSaveAndLeave}
        onLeaveWithoutSaving={handleLeaveWithoutSaving}
        isTimed={isTimed}
        timeLeft={warningTimeLeft}
      />
    </div>
  );
};

export default EvaluatorEvaluationForm; 